import React, { useCallback, useMemo, useRef, useState } from 'react';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import DropdownMenu, { type CustomTriggerProps } from '@atlaskit/dropdown-menu';
import { HeadingItem, MenuGroup } from '@atlaskit/menu';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { ReasoningModeMenuItemComponent } from './ReasoningModeMenuItem';
import type { ReasoningModeMenuProps } from './types';

const styles = cssMap({
	menuContainer: {
		paddingBlock: token('space.100'),
		paddingInline: token('space.100'),
	},
	heading: {
		paddingTop: token('space.050'),
	},
	footer: {
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
		paddingTop: token('space.050'),
		marginTop: token('space.050'),
	},
});

export const ReasoningModeMenu = ({
	triggerLabel,
	triggerIcon,
	triggerTestId = 'reasoning-mode-menu-trigger',
	items,
	isDisabled = false,
	isOpen: isOpenProp,
	onOpenChange: onOpenChangeProp,
	placement = 'top-start',
	shouldRenderToParent = true,
	menuWidth = 360,
	renderFooter,
	headingLabel,
	triggerWrapper: TriggerWrapper,
}: ReasoningModeMenuProps): React.JSX.Element => {
	const isControlled = isOpenProp !== undefined;
	const [isLocalOpen, setIsLocalOpen] = useState(false);
	const isControlledRef = useRef(isControlled);
	isControlledRef.current = isControlled;
	const isOpen = isControlled ? isOpenProp : isLocalOpen;

	const onOpenChange = useCallback(
		(attrs: { isOpen: boolean }) => {
			if (!isControlledRef.current) {
				setIsLocalOpen(attrs.isOpen);
			}
			onOpenChangeProp?.(attrs);
		},
		[onOpenChangeProp],
	);
	const wrappedItems = useMemo(
		() =>
			items.map((item) => ({
				...item,
				onSelect: () => {
					item.onSelect();
					onOpenChange({ isOpen: false });
				},
			})),
		[items, onOpenChange],
	);

	const trigger = useCallback(
		({ triggerRef, onClick, ...triggerProps }: CustomTriggerProps) => {
			const button = (
				<Button
					{...triggerProps}
					ref={triggerRef}
					appearance="subtle"
					iconBefore={triggerIcon}
					isDisabled={isDisabled}
					onClick={onClick}
					testId={triggerTestId}
				>
					{triggerLabel}
				</Button>
			);

			if (TriggerWrapper) {
				return <TriggerWrapper>{button}</TriggerWrapper>;
			}

			return button;
		},
		[TriggerWrapper, isDisabled, triggerIcon, triggerLabel, triggerTestId],
	);

	const footerContent = renderFooter?.();

	return (
		<DropdownMenu
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			placement={placement}
			shouldRenderToParent={shouldRenderToParent}
			trigger={trigger}
			label={headingLabel}
		>
			<MenuGroup maxWidth={menuWidth} minWidth={menuWidth}>
				<Box xcss={styles.menuContainer}>
					{headingLabel ? (
						<Stack space="space.100">
							<Box xcss={styles.heading}>
								<HeadingItem aria-hidden>{headingLabel}</HeadingItem>
							</Box>
							<Box>
								{wrappedItems.map((item) => (
									<ReasoningModeMenuItemComponent key={item.id} {...item} />
								))}
							</Box>
						</Stack>
					) : (
						wrappedItems.map((item) => <ReasoningModeMenuItemComponent key={item.id} {...item} />)
					)}
					{footerContent != null && footerContent !== false && (
						<Box xcss={styles.footer}>{footerContent}</Box>
					)}
				</Box>
			</MenuGroup>
		</DropdownMenu>
	);
};
